// src/data/examParts.js
// Treść dziewięciu zestawów pytań egzaminacyjnych trzymana jako dane, a nie JSX.
//
// Dzięki temu jeden komponent renderuje wszystkie części, a z tych samych danych
// powstają dane strukturalne FAQPage - wcześniej pytania i odpowiedzi były
// zaszyte w markupie i Google nie miał z nich żadnego rozszerzonego wyniku.
//
// Z treści usunięto ozdobne emoji (📘, 🔫, 🔸 ...), które w wielu przeglądarkach
// renderowały się jako puste prostokąty.

export const EXAM_PARTS = [
  {
    path: "/pytania-na-pozwolenie-na-bron-patent-strzelecki",
    part: 1,
    h1: "Pytania na patent strzelecki 2026 - broń i amunicja (część 1)",
    lead: "Pierwszy zestaw obejmuje definicje z ustawy o broni i amunicji: co jest amunicją, czym jest broń szczególnie niebezpieczna, na jakiej podstawie można kupić amunicję i w jakim terminie trzeba zarejestrować broń. Od tych pojęć zaczyna się egzamin teoretyczny PZSS i wracają one w kolejnych częściach.",
    summary: "Definicje z tej części są fundamentem - większość późniejszych pytań tylko do nich nawiązuje. Jeśli pewnie odróżniasz amunicję od jej istotnych części, a broń palną od pneumatycznej, kolejne zestawy pójdą znacznie szybciej.",
    related: [{"to":"/patent-strzelecki","label":"Patent strzelecki - wymagania i przebieg egzaminu"},{"href":"/egzamin/budowa-broni-i-amunicji-kompendium-dla-strzelcow","label":"Budowa broni i amunicji - kompendium"}],
    sections: [
      {
        id: "bron-i-amunicja-definicje-i-aktualne-podstawy-prawne",
        heading: "Broń i amunicja - definicje i aktualne podstawy prawne",
        questions: [
          {
            slug: "pociski-zaliczaja-sie-do-amunicji",
            q: "Jakie pociski zaliczają się do amunicji?",
            blocks: [
              { type: 'p', text: "Amunicją są naboje przeznaczone do strzelania z broni palnej, które zawierają komplet elementów umożliwiających oddanie strzału, czyli m.in. pocisk, ładunek miotający oraz spłonkę." },
              { type: 'p', text: "Za amunicję uznaje się zarówno naboje bojowe, sportowe, jak i szkoleniowe, o ile są one przeznaczone do użycia w broni palnej. Same pociski (bez łuski i spłonki) co do zasady nie stanowią amunicji, chyba że przepisy szczególne stanowią inaczej." },
            ],
          },
          {
            slug: "co-to-jest-amunicja-szczegolnie-niebezpieczna",
            q: "Co to jest amunicja szczególnie niebezpieczna?",
            blocks: [
              { type: 'p', text: "Amunicja szczególnie niebezpieczna to rodzaj amunicji, który ze względu na swoje właściwości stwarza podwyższone zagrożenie dla życia, zdrowia lub bezpieczeństwa publicznego." },
              { type: 'p', text: "Do amunicji szczególnie niebezpiecznej zalicza się w szczególności:" },
              { type: 'ul', items: ["amunicję z pociskami podkalibrowymi, w tym wykonanymi z tworzyw sztucznych,","amunicję zawierającą materiały wybuchowe, zapalające lub smugowe,","amunicję wytworzoną w sposób niefabryczny lub przerobioną w sposób zmieniający jej pierwotne właściwości."] },
              { type: 'p', text: "Obrót i posiadanie takiej amunicji podlega szczególnym ograniczeniom i wymaga odrębnych uprawnień." },
            ],
          },
          {
            slug: "na-jakiej-podstawie-mozna-nabyc-amunicje",
            q: "Na jakiej podstawie można nabyć amunicję?",
            blocks: [
              { type: 'p', text: "Nabycie amunicji jest możliwe wyłącznie przez osoby lub podmioty posiadające odpowiednie uprawnienia." },
              { type: 'ul', items: ["na podstawie legitymacji posiadacza broni - w zakresie kalibrów i rodzajów broni, do których dana osoba posiada pozwolenie,","na podstawie pisemnego zamówienia złożonego przez uprawniony podmiot prowadzący obrót bronią lub amunicją."] },
              { type: 'p', text: "Sprzedawca ma obowiązek zweryfikować uprawnienia kupującego przed wydaniem amunicji." },
            ],
          },
          {
            slug: "mozna-przenosic-z-bronia-palna-do-celow-sportowych",
            q: "Co można przenosić z bronią palną do celów sportowych?",
            blocks: [
              { type: 'p', text: "Przenoszenie broni palnej do celów sportowych musi odbywać się w sposób zapewniający bezpieczeństwo." },
              { type: 'p', text: "Broń w czasie przenoszenia musi być:" },
              { type: 'ul', items: ["rozładowana, czyli bez amunicji w komorze nabojowej,","z magazynkami niezaładowanymi amunicją."] },
              { type: 'p', text: "Dodatkowo broń powinna być przenoszona w pokrowcu, futerale lub innym zabezpieczonym opakowaniu, uniemożliwiającym jej przypadkowe użycie." },
            ],
          },
          {
            slug: "nalezy-zarejestrowac-zakupiona-bron",
            q: "Kiedy należy zarejestrować zakupioną broń?",
            blocks: [
              { type: 'p', text: "Zakupiona broń palna podlega obowiązkowej rejestracji. Rejestracji należy dokonać w terminie 5 dni od dnia zakupu, jeżeli broń została nabyta na podstawie zaświadczenia uprawniającego do jej zakupu." },
              { type: 'p', text: "Rejestracji dokonuje się w właściwej jednostce Policji, a niedotrzymanie terminu może skutkować konsekwencjami administracyjnymi." },
            ],
          },
          {
            slug: "rodzaje-broni-uznaje-sie-za-bron-palna",
            q: "Jakie rodzaje broni uznaje się za broń palną?",
            blocks: [
              { type: 'ul', items: ["bojową,","myśliwską,","sportową,","gazową,","alarmową,","sygnałową."] },
            ],
          },
          {
            slug: "czym-jest-bron-pneumatyczna",
            q: "Czym jest broń pneumatyczna?",
            blocks: [
              { type: 'p', text: "To urządzenie miotające pocisk energią sprężonego gazu, osiągające powyżej 17 J. Posiada celownik i może razić cel na odległość." },
            ],
          },
          {
            slug: "czym-jest-bron-szczegolnie-niebezpieczna",
            q: "Czym jest broń szczególnie niebezpieczna?",
            blocks: [
              { type: 'ul', items: ["broń samoczynna,","broń z tłumikiem huku,","broń bez cech identyfikacyjnych,","broń z ukrytymi mechanizmami strzelania lub zamaskowana."] },
            ],
          },
        ],
      },
      {
        id: "posiadanie-i-obrot-bronia",
        heading: "Posiadanie i obrót bronią",
        questions: [
          {
            slug: "moze-uzywac-broni-sportowej-klubu",
            q: "Kto może używać broni sportowej klubu?",
            blocks: [
              { type: 'p', text: "Osoby nieposiadające pozwolenia - tylko w obecności instruktora lub osoby uprawnionej w ramach działalności klubu/stowarzyszenia." },
            ],
          },
          {
            slug: "celow-mozna-uzywac-broni-palnej",
            q: "Do jakich celów można używać broni palnej?",
            blocks: [
              { type: 'p', text: "Do celów: szkoleniowych, sportowych, rekreacyjnych oraz pokazowych - zgodnie z pozwoleniem." },
            ],
          },
          {
            slug: "moze-posiadac-bron-palna",
            q: "Kto może posiadać broń palną?",
            blocks: [
              { type: 'ul', items: ["osoby cywilne - za zgodą komendanta wojewódzkiego Policji,","żołnierze zawodowi - za zgodą komendanta Żandarmerii Wojskowej."] },
            ],
          },
          {
            slug: "mozna-posiadac-bron-pneumatyczna",
            q: "Na jakiej podstawie można posiadać broń pneumatyczną?",
            blocks: [
              { type: 'ul', items: ["karta rejestracyjna z komendy Policji - dla cywilów,","decyzja Żandarmerii Wojskowej - dla żołnierzy."] },
            ],
          },
          {
            slug: "mozna-zbywac-bron-lub-amunicje-innym-osobom",
            q: "Czy można zbywać broń lub amunicję innym osobom?",
            blocks: [
              { type: 'p', text: "Tak - ale tylko między osobami posiadającymi pozwolenie na ten sam rodzaj broni." },
            ],
          },
          {
            slug: "dozwolone-sa-przerobki-broni",
            q: "Czy dozwolone są przeróbki broni?",
            blocks: [
              { type: 'p', text: "Nie - przerabianie rodzaju, kalibru lub przeznaczenia broni jest zakazane." },
            ],
          },
          {
            slug: "istotne-czesci-broni-lub-amunicji-sa-traktowane-jak-bron",
            q: "Czy istotne części broni lub amunicji są traktowane jak broń?",
            blocks: [
              { type: 'p', text: "Tak - zgodnie z przepisami są uznawane za broń lub amunicję i podlegają rejestracji oraz kontroli." },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/egzamin-patent-strzelecki-czesc-druga",
    part: 2,
    h1: "Egzamin na patent strzelecki 2026 - przepisy i procedury (część 2)",
    lead: "Druga część dotyczy procedur: badań lekarskich i psychologicznych, uprawnień Policji, deponowania broni oraz warunków formalnych - wieku, terminu rejestracji i opłaty za egzamin. To pytania, na których najczęściej wykłada się osoby uczące się wyłącznie „technicznej\" strony broni.",
    summary: "Procedury są łatwe do zapamiętania, bo opierają się na konkretnych terminach i liczbach. Warto wracać do nich tuż przed egzaminem - to najczęściej mylone odpowiedzi w całym teście.",
    related: [{"to":"/patent-strzelecki","label":"Jak uzyskać pozwolenie na broń - krok po kroku"},{"href":"/egzamin/wszystko-o-patencie-i-licencji-pzss","label":"Patent i licencja PZSS - czym się różnią"}],
    sections: [
      {
        id: "badania-lekarskie-i-uprawnienia-policji",
        heading: "Badania lekarskie i uprawnienia Policji",
        questions: [
          {
            slug: "policja-moze-zobowiazac-posiadacza-pozwolenia-na-bron-do",
            q: "Czy Policja może zobowiązać posiadacza pozwolenia na broń do badań lekarskich i psychologicznych?",
            blocks: [
              { type: 'p', text: "Tak. Organy Policji mają takie prawo, jeżeli istnieją uzasadnione przesłanki, że dana osoba może mieć problemy psychiczne, uzależnienie od alkoholu lub innych substancji, lub zaburzenia ograniczające zdolność do bezpiecznego posługiwania się bronią." },
            ],
          },
          {
            slug: "policja-moze-ograniczyc-lub-zakazac-noszenia-broni-mimo",
            q: "Czy Policja może ograniczyć lub zakazać noszenia broni mimo posiadania pozwolenia?",
            blocks: [
              { type: 'p', text: "Tak. Posiadanie pozwolenia nie oznacza automatycznie prawa do noszenia broni przy sobie. Organ Policji może nałożyć ograniczenia lub całkowicie zakazać noszenia w interesie bezpieczeństwa publicznego." },
            ],
          },
          {
            slug: "osoba-deponujaca-bron-w-jednostce-policji-moze-ja-okazac",
            q: "Czy osoba deponująca broń w jednostce Policji może ją okazać potencjalnemu nabywcy?",
            blocks: [
              { type: 'p', text: "Tak, ale tylko po uzgodnieniu z jednostką Policji, w której broń została zdeponowana. Dostęp do broni w celu sprzedaży jest możliwy, jednak wymaga zgody." },
            ],
          },
          {
            slug: "posiadacz-pozwolenia-sportowego-musi-dostarczac-aktualne",
            q: "Czy posiadacz pozwolenia sportowego musi dostarczać aktualne orzeczenia lekarskie do Policji?",
            blocks: [
              { type: 'p', text: "Nie. Po uzyskaniu pozwolenia nie ma obowiązku okresowego dostarczania orzeczeń, chyba że Policja wyda takie polecenie w konkretnych okolicznościach." },
            ],
          },
          {
            slug: "do-wniosku-o-wydanie-pozwolenia-na-bron-trzeba-dolaczyc",
            q: "Czy do wniosku o wydanie pozwolenia na broń trzeba dołączyć orzeczenia?",
            blocks: [
              { type: 'p', text: "Tak. Do wniosku należy dołączyć zarówno orzeczenie lekarskie, jak i psychologiczne potwierdzające brak przeciwwskazań do posiadania broni." },
            ],
          },
          {
            slug: "policja-moze-cofnac-pozwolenie-osobie-uchylajacej-sie-od",
            q: "Czy Policja może cofnąć pozwolenie osobie uchylającej się od badań?",
            blocks: [
              { type: 'p', text: "Tak. Odmowa poddania się badaniom po wezwaniu przez Policję jest podstawą do cofnięcia pozwolenia na broń." },
            ],
          },
        ],
      },
      {
        id: "amunicja-istotne-czesci-i-definicja-broni-palnej",
        heading: "Amunicja, istotne części i definicja broni palnej",
        questions: [
          {
            slug: "mozna-posiadac-amunicje-wykonana-niefabrycznie",
            q: "Czy można posiadać amunicję wykonaną niefabrycznie?",
            blocks: [
              { type: 'p', text: "Tak, ale wyłącznie do użytku własnego, i tylko przez osoby posiadające pozwolenie na broń myśliwską, sportową lub kolekcjonerską. Nie wolno takiej amunicji zbywać." },
            ],
          },
          {
            slug: "kolekcjonerzy-moga-strzelac-z-posiadanej-broni",
            q: "Czy kolekcjonerzy mogą strzelać z posiadanej broni?",
            blocks: [
              { type: 'p', text: "Nie. Pozwolenie kolekcjonerskie nie uprawnia do użytkowania broni na strzelnicy. Do tego potrzebne jest oddzielne pozwolenie sportowe lub myśliwskie." },
            ],
          },
          {
            slug: "elementy-uwaza-sie-za-istotne-czesci-amunicji",
            q: "Jakie elementy uważa się za istotne części amunicji?",
            blocks: [
              { type: 'p', text: "M.in. pociski zawierające materiały wybuchowe lub chemiczne, spłonki oraz proch. Każdy z tych elementów może być traktowany jako amunicja i podlega ścisłej regulacji prawnej." },
            ],
          },
          {
            slug: "jest-istotna-czescia-broni-palnej",
            q: "Co jest istotną częścią broni palnej?",
            blocks: [
              { type: 'p', text: "Elementy takie jak: zamek, szkielet, lufa z komorą nabojową, komora zamkowa, bęben czy baskila. Są one traktowane jak broń i ich posiadanie bez pozwolenia jest zabronione." },
            ],
          },
          {
            slug: "nalezy-zrobic-z-bronia-po-utracie-uprawnien-do-jej",
            q: "Co należy zrobić z bronią po utracie uprawnień do jej posiadania?",
            blocks: [
              { type: 'p', text: "Broń oraz amunicję należy w ciągu 30 dni zdeponować w jednostce Policji. Niedopełnienie tego obowiązku jest wykroczeniem." },
            ],
          },
          {
            slug: "typy-broni-mieszcza-sie-w-definicji-broni-palnej",
            q: "Które typy broni mieszczą się w definicji broni palnej?",
            blocks: [
              { type: 'p', text: "Broń bojowa, myśliwska, sportowa, gazowa, alarmowa i sygnałowa. Każda z nich podlega rejestracji i wymaga odpowiedniego pozwolenia." },
            ],
          },
        ],
      },
      {
        id: "wiek-rejestracja-i-oplata-egzaminacyjna",
        heading: "Wiek, rejestracja i opłata egzaminacyjna",
        questions: [
          {
            slug: "wieku-mozna-uzyskac-pozwolenie-na-bron-do-celow-sportowych",
            q: "Od jakiego wieku można uzyskać pozwolenie na broń do celów sportowych/łowieckich?",
            blocks: [
              { type: 'p', text: "Od 18 roku życia, na wniosek szkoły, klubu sportowego lub organizacji. Osoba musi również spełniać pozostałe wymogi ustawowe." },
            ],
          },
          {
            slug: "nalezy-zarejestrowac-nowo-nabyta-bron",
            q: "W jakim terminie należy zarejestrować nowo nabytą broń?",
            blocks: [
              { type: 'p', text: "W ciągu 5 dni od daty zakupu, w odpowiedniej jednostce Policji. Termin ten jest nieprzekraczalny." },
            ],
          },
          {
            slug: "jest-zwolniony-z-egzaminu-na-patent-strzelecki",
            q: "Kto jest zwolniony z egzaminu na patent strzelecki?",
            blocks: [
              { type: 'p', text: "Funkcjonariusze Policji, wojska, służb specjalnych oraz członkowie PZSS z licencją strzelecką, którzy zdali egzamin na podstawie innych przepisów." },
            ],
          },
          {
            slug: "jest-oplata-za-egzamin-strzelecki",
            q: "Jaka jest opłata za egzamin strzelecki?",
            blocks: [
              { type: 'p', text: "400 zł - zgodnie z ustawą z dnia 25 czerwca 2010 r. o sporcie (Dz.U. z 2023 r. poz. 2048). Opłata jest jednorazowa i obowiązkowa." },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/sprawdzenie-wiedzy-do-patentu-strzeleckiego",
    part: 3,
    h1: "Pytania egzaminacyjne na broń - przechowywanie i rejestracja (część 3)",
    lead: "Trzecia część to obowiązki posiadacza broni: przechowywanie, rejestracja, zgłaszanie zmian i sytuacje, w których pozwolenie można stracić. Pytania z tego zakresu pojawiają się na egzaminie niemal zawsze, bo dotyczą codziennych obowiązków każdego strzelca.",
    summary: "Tę część najłatwiej zapamiętać przez skojarzenie z praktyką: gdzie trzymasz broń, komu i w jakim terminie zgłaszasz zmiany, co robisz po utracie uprawnień.",
    related: [{"href":"/egzamin/przechowywanie-i-noszenie-broni-w-polsce","label":"Przechowywanie i noszenie broni w Polsce"},{"to":"/patent-strzelecki","label":"Patent strzelecki - wymagania i przebieg egzaminu"}],
    sections: [
      {
        id: "przepisy-i-obowiazki-posiadacza-broni",
        heading: "Przepisy i obowiązki posiadacza broni",
        questions: [
          {
            slug: "policja-obligatoryjnie-cofa-pozwolenie-na-bron",
            q: "Kiedy Policja obligatoryjnie cofa pozwolenie na broń?",
            blocks: [
              { type: 'p', text: "Art. 18 ust. 1 ustawy o broni i amunicji wymienia cztery przypadki, w których organ Policji cofa pozwolenie obowiązkowo. Katalog jest zamknięty." },
              { type: 'p', text: "Organ cofa pozwolenie, jeżeli posiadacz:" },
              { type: 'ul', items: ["nie przestrzega warunków określonych w pozwoleniu, o których mowa w art. 10 ust. 7, czyli ograniczenia albo wykluczenia noszenia broni potwierdzonego w legitymacji posiadacza broni,","należy do osób wymienionych w art. 15 ust. 1 pkt 2-6: z zaburzeniami psychicznymi lub znacznie ograniczoną sprawnością psychofizyczną, z istotnymi zaburzeniami funkcjonowania psychologicznego, uzależnionych od alkoholu lub substancji psychoaktywnych, bez miejsca stałego pobytu w Polsce, a także prawomocnie skazanych w sposób opisany w pkt 6,","naruszył obowiązek zawiadomienia o utracie broni z art. 25, czyli nie powiadomił Policji albo Żandarmerii Wojskowej niezwłocznie, nie później niż w ciągu 24 godzin od stwierdzenia utraty,","przemieszcza się z rozładowaną bronią albo nosi broń, znajdując się w stanie po użyciu alkoholu, środka odurzającego, substancji psychotropowej albo środka zastępczego."] },
              { type: 'p', text: "Od tego trzeba odróżnić cofnięcie fakultatywne. Zgodnie z art. 18 ust. 4 organ może cofnąć pozwolenie, jeżeli ustały okoliczności faktyczne, które stanowiły podstawę jego wydania, a art. 18 ust. 5 wymienia dalsze przypadki, w których cofnięcie zależy od oceny organu, na przykład naruszenie obowiązku rejestracji broni." },
            ],
          },
          {
            slug: "nalezy-zglosic-zmiane-miejsca-stalego-pobytu",
            q: "Czy należy zgłosić zmianę miejsca stałego pobytu?",
            blocks: [
              { type: 'p', text: "Tak, w terminie 14 dni należy pisemnie poinformować organ Policji właściwy dla nowego miejsca zamieszkania. Obowiązek ten wynika z potrzeby aktualizacji danych posiadacza broni." },
            ],
          },
          {
            slug: "nalezy-zrobic-po-utracie-uprawnien-do-posiadania-broni",
            q: "Co należy zrobić po utracie uprawnień do posiadania broni?",
            blocks: [
              { type: 'p', text: "Osoba traci prawo do posiadania broni i ma obowiązek niezwłocznie ją zbyć wraz z amunicją - np. sprzedać lub przekazać osobie posiadającej stosowne pozwolenie." },
            ],
          },
          {
            slug: "naruszenie-ograniczenia-noszenia-broni-jest-przestepstwem",
            q: "Czy naruszenie ograniczenia noszenia broni jest przestępstwem?",
            blocks: [
              { type: 'p', text: "Tak, osoby noszące broń wbrew zakazowi lub ograniczeniu popełniają czyn zabroniony. Policja może cofnąć pozwolenie w takiej sytuacji." },
            ],
          },
          {
            slug: "nalezy-przechowywac-bron-i-amunicje",
            q: "Jak należy przechowywać broń i amunicję?",
            blocks: [
              { type: 'p', text: "W stalowych kasach lub szafach przymocowanych do elementów konstrukcyjnych budynku lub w metalowych sejfach z odpowiednimi atestami. To wymóg wynikający z przepisów o bezpiecznym przechowywaniu." },
            ],
          },
          {
            slug: "nalezy-zglosic-utrate-broni",
            q: "W jakim terminie należy zgłosić utratę broni?",
            blocks: [
              { type: 'p', text: "Zgłoszenia należy dokonać niezwłocznie, nie później niż w ciągu 24 godzin od chwili stwierdzenia utraty broni." },
            ],
          },
        ],
      },
      {
        id: "ograniczenia-i-wyjatki-w-posiadaniu-broni",
        heading: "Ograniczenia i wyjątki w posiadaniu broni",
        questions: [
          {
            slug: "nie-moze-otrzymac-pozwolenia-na-bron",
            q: "Kto nie może otrzymać pozwolenia na broń?",
            blocks: [
              { type: 'p', text: "Osoby poniżej 18 roku życia oraz osoby z zaburzeniami psychicznymi są ustawowo wykluczone z możliwości uzyskania pozwolenia na broń." },
            ],
          },
          {
            slug: "w-jakich-przypadkach-pozwolenie-na-bron-nie-jest-wymagane",
            q: "W jakich przypadkach pozwolenie na broń nie jest wymagane?",
            blocks: [
              { type: 'p', text: "Pozwolenie nie jest wymagane m.in. przy posiadaniu broni muzealnej, broni na strzelnicy, broni alarmowej do 6 mm, broni pozbawionej cech użytkowych, czy też broni czarnoprochowej sprzed 1885 r. oraz jej replik." },
            ],
          },
          {
            slug: "jest-minimalny-wiek-do-uzyskania-pozwolenia-z-wyjatkiem",
            q: "Jaki jest minimalny wiek do uzyskania pozwolenia (z wyjątkiem sportowego i myśliwskiego)?",
            blocks: [
              { type: 'p', text: "Wnioskodawca musi mieć ukończone 21 lat. To ograniczenie wiekowe ma na celu zapewnienie dojrzałości i odpowiedzialności posiadacza broni." },
            ],
          },
        ],
      },
      {
        id: "szczegoly-dotyczace-poszczegolnych-typow-broni-i-procedur",
        heading: "Szczegóły dotyczące poszczególnych typów broni i procedur",
        questions: [
          {
            slug: "do-czego-uprawnia-pozwolenie-na-bron-sportowa",
            q: "Do czego uprawnia pozwolenie na broń sportową?",
            blocks: [
              { type: 'p', text: "Do posiadania broni palnej bocznego zapłonu z lufami gwintowanymi o kalibrze do 6 mm, zgodnie z ustawą o broni i amunicji." },
            ],
          },
          {
            slug: "mozna-przesylac-bron-i-amunicje-kurierem",
            q: "Czy można przesyłać broń i amunicję kurierem?",
            blocks: [
              { type: 'p', text: "Tak, ale wyłącznie za pośrednictwem firm specjalizujących się w przewozie tego rodzaju towarów i posiadających odpowiednie zezwolenia." },
            ],
          },
          {
            slug: "dokumenty-sa-potrzebne-do-przywozu-broni-spoza-ue",
            q: "Jakie dokumenty są potrzebne do przywozu broni spoza UE?",
            blocks: [
              { type: 'p', text: "Obywatel Polski musi posiadać zaświadczenie wydane przez właściwego konsula RP. Brak dokumentu skutkuje niedopuszczeniem broni do wwozu." },
            ],
          },
          {
            slug: "na-podstawie-jakiego-dokumentu-rejestruje-sie-bron",
            q: "Na podstawie jakiego dokumentu rejestruje się broń?",
            blocks: [
              { type: 'p', text: "Na podstawie dowodu nabycia broni (np. faktury, umowy sprzedaży) odpowiedni organ Policji lub Żandarmeria Wojskowa dokonuje rejestracji." },
            ],
          },
          {
            slug: "grozi-za-niezgloszenie-zamiaru-wywozu-broni-za-granice",
            q: "Co grozi za niezgłoszenie zamiaru wywozu broni za granicę?",
            blocks: [
              { type: 'p', text: "W przypadku naruszenia wymogu uzyskania zgody, Policja może cofnąć pozwolenie na broń, traktując to jako poważne naruszenie przepisów." },
            ],
          },
          {
            slug: "grozi-za-uzyczenie-broni-osobie-nieuprawnionej",
            q: "Co grozi za użyczenie broni osobie nieuprawnionej?",
            blocks: [
              { type: 'p', text: "Jest to czyn zabroniony i podlega karze grzywny lub aresztu. Przekazanie broni osobie bez stosownych uprawnień jest traktowane jako zagrożenie bezpieczeństwa." },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/odpowiedzi-egzamin-na-bron-wersja-trzecia",
    part: 4,
    h1: "Egzamin na broń - transport i bezpieczne użytkowanie broni (część 4)",
    lead: "Czwarta część obejmuje noszenie, przewożenie i używanie broni: dokumenty wymagane przy pozwoleniu, zasady transportu środkami publicznymi oraz bezpieczne obchodzenie się z bronią poza strzelnicą. To pytania o sytuacje, w których najczęściej dochodzi do naruszeń przepisów.",
    summary: "Zasady transportu i noszenia broni są egzekwowane w praktyce - ich nieznajomość kosztuje nie tylko punkty na egzaminie, ale i pozwolenie.",
    related: [{"href":"/egzamin/przewoz-broni-transportem-publicznym","label":"Przewóz broni transportem publicznym"},{"href":"/egzamin/przechowywanie-i-noszenie-broni-w-polsce","label":"Przechowywanie i noszenie broni w Polsce"}],
    sections: [
      {
        id: "kryteria-i-dokumenty-zwiazane-z-pozwoleniem",
        heading: "Kryteria i dokumenty związane z pozwoleniem",
        questions: [
          {
            slug: "uznaje-sie-za-wazna-przyczyne-wydania-pozwolenia-sportowego",
            q: "Co uznaje się za ważną przyczynę wydania pozwolenia sportowego?",
            blocks: [
              { type: 'p', text: "Za ważną przyczynę do uzyskania pozwolenia na broń do celów sportowych uznaje się aktywność w klubie strzeleckim, posiadanie odpowiednich kwalifikacji sportowych, takich jak uprawnienia zawodnika lub sędziego strzelectwa, a także aktualną licencję wydaną przez właściwy związek sportowy. Kandydat musi wykazać rzeczywiste zaangażowanie w działalność strzelecką, a nie jedynie formalną przynależność do organizacji." },
            ],
          },
          {
            slug: "co-potwierdza-zarejestrowanie-broni",
            q: "Co potwierdza zarejestrowanie broni?",
            blocks: [
              { type: 'p', text: "Zarejestrowanie broni palnej potwierdzają dwa dokumenty: legitymacja posiadacza broni oraz świadectwo broni. Legitymacja potwierdza uprawnienie do posiadania konkretnej sztuki broni, natomiast świadectwo broni zawiera szczegółowe dane identyfikacyjne broni i jest dokumentem ewidencyjnym, wymaganym przy jej przewożeniu lub prezentowaniu organom kontrolnym." },
            ],
          },
          {
            slug: "dlugo-wazna-jest-legitymacja-uprawniajaca-do-nabycia-broni",
            q: "Jak długo ważna jest legitymacja uprawniająca do nabycia broni?",
            blocks: [
              { type: 'p', text: "Legitymacja uprawniająca do nabycia broni, wydawana przez właściwy organ Policji, jest ważna przez okres 3 miesięcy od dnia jej wystawienia. Po upływie tego terminu dokument traci moc, a osoba zainteresowana zakupem broni musi wystąpić o wydanie nowej legitymacji. Przekroczenie terminu uniemożliwia legalne nabycie broni na podstawie wygasłego dokumentu." },
            ],
          },
          {
            slug: "dlugo-wazne-jest-swiadectwo-do-nabycia-broni",
            q: "Jak długo ważne jest świadectwo do nabycia broni?",
            blocks: [
              { type: 'p', text: "Świadectwo uprawniające do nabycia określonej sztuki broni palnej zachowuje ważność przez 3 miesiące od dnia jego wystawienia. Jeżeli w tym czasie osoba nie sfinalizuje zakupu i nie przeprowadzi rejestracji broni, świadectwo traci ważność, a dalsze działania wymagają ponownego wystąpienia o nowy dokument. Terminowość jest tutaj kluczowa, by uniknąć dodatkowych formalności." },
            ],
          },
        ],
      },
      {
        id: "bezpieczenstwo-na-strzelnicy-i-w-transporcie",
        heading: "Bezpieczeństwo na strzelnicy i w transporcie",
        questions: [
          {
            slug: "trzeba-zglosic-zaginiecie-broni",
            q: "W jakim terminie trzeba zgłosić zaginięcie broni?",
            blocks: [
              { type: 'p', text: "W przypadku zaginięcia broni lub podejrzenia jej utraty, należy natychmiast, bez zbędnej zwłoki, zawiadomić właściwy organ Policji. Zgłoszenie powinno być dokonane najpóźniej w ciągu 24 godzin od momentu stwierdzenia braku broni, aby ograniczyć ryzyko jej nieuprawnionego użycia i wynikających z tego zagrożeń." },
            ],
          },
          {
            slug: "moze-przebywac-na-stanowisku-strzeleckim",
            q: "Kto może przebywać na stanowisku strzeleckim?",
            blocks: [
              { type: 'p', text: "Na stanowiskach strzeleckich mogą przebywać wyłącznie osoby, które aktualnie korzystają ze strzelnicy oraz realizują strzelanie zgodnie z obowiązującymi zasadami bezpieczeństwa. Wstęp dla osób postronnych lub obserwatorów na stanowisko jest zabroniony, aby zminimalizować ryzyko wypadków." },
            ],
          },
          {
            slug: "bron-powinna-byc-przechowywana-poza-stanowiskiem",
            q: "Jak broń powinna być przechowywana poza stanowiskiem?",
            blocks: [
              { type: 'p', text: "Poza stanowiskiem strzeleckim broń musi być zawsze rozładowana, z otwartą komorą nabojową oraz bez zamocowanego pasa czy pokrowca. Broń powinna być przenoszona w sposób widoczny, aby wszyscy obecni mogli upewnić się, że jest bezpieczna i nie stanowi zagrożenia." },
            ],
          },
          {
            slug: "przewozic-bron-w-komunikacji-publicznej",
            q: "Jak przewozić broń w komunikacji publicznej?",
            blocks: [
              { type: 'p', text: "Przewożąc broń w środkach transportu publicznego, należy upewnić się, że jest całkowicie rozładowana - zarówno komora nabojowa, jak i magazynki muszą być puste. Broń powinna być zabezpieczona w pokrowcu lub futerale i nie może być łatwo dostępna dla osób postronnych." },
            ],
          },
          {
            slug: "zabezpieczyc-bron-sportowa-w-transporcie-publicznym",
            q: "Jak zabezpieczyć broń sportową w transporcie publicznym?",
            blocks: [
              { type: 'p', text: "Broń sportowa przewożona środkami transportu publicznego musi być odpowiednio zabezpieczona, tak aby uniemożliwić jej przypadkowe użycie lub dostęp osobom nieuprawnionym. Transport musi odbywać się w sposób, który nie stwarza zagrożenia dla życia, zdrowia lub mienia." },
            ],
          },
          {
            slug: "dokumenty-trzeba-miec-przy-przewozie-broni-sportowej",
            q: "Jakie dokumenty trzeba mieć przy przewozie broni sportowej?",
            blocks: [
              { type: 'p', text: "Przewóz broni sportowej jest dozwolony pod warunkiem posiadania przy sobie wymaganych dokumentów - takich jak licencja sportowa i pozwolenie na broń. Broń powinna być transportowana w futerale lub pokrowcu, traktowana jako oddzielny bagaż i przewożona w sposób zapewniający bezpieczeństwo." },
            ],
          },
          {
            slug: "mozna-korzystac-z-cudzej-broni-na-strzelnicy",
            q: "Czy można korzystać z cudzej broni na strzelnicy?",
            blocks: [
              { type: 'p', text: "Korzystanie z broni należącej do innej osoby na strzelnicy jest możliwe wyłącznie po uzyskaniu jej wyraźnej zgody. Użytkownik cudzej broni powinien przestrzegać wszystkich zasad bezpieczeństwa i ponosić odpowiedzialność za prawidłowe obchodzenie się z nią podczas użytkowania." },
            ],
          },
          {
            slug: "na-strzelnicy-wolno-spozywac-alkohol-lub-srodki-odurzajace",
            q: "Czy na strzelnicy wolno spożywać alkohol lub środki odurzające?",
            blocks: [
              { type: 'p', text: "Bezwzględnie zabronione jest spożywanie alkoholu oraz przebywanie pod wpływem środków odurzających lub psychotropowych na terenie strzelnicy. Osoby naruszające ten zakaz mogą zostać natychmiast usunięte z obiektu, a także ponieść konsekwencje prawne, w tym cofnięcie pozwolenia na broń." },
            ],
          },
          {
            slug: "mozna-wyjac-bron-na-strzelnicy",
            q: "Gdzie można wyjąć broń na strzelnicy?",
            blocks: [
              { type: 'p', text: "Wyjmowanie broni z futerału lub kabury jest dozwolone wyłącznie bezpośrednio na stanowisku strzeleckim, pod warunkiem, że użytkownik jest przygotowany do rozpoczęcia strzelania. Wyciąganie lub manipulowanie bronią w innych częściach strzelnicy jest zabronione i grozi natychmiastowym wydaleniem z obiektu." },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/praktyka-i-teoria-patentu-strzeleckiego",
    part: 5,
    h1: "Patent strzelecki - zasady bezpieczeństwa na strzelnicy (część 5)",
    lead: "Piąta część to zasady bezpieczeństwa na strzelnicy: komendy prowadzącego strzelanie, obowiązki uczestników i sytuacje, w których strzelanie trzeba natychmiast przerwać. Tej wiedzy egzaminatorzy pilnują najbardziej, bo od niej zależy bezpieczeństwo na osi.",
    summary: "Komendy warto znać odruchowo, a nie „z pamięci\". Na strzelnicy reaguje się w sekundę, a na egzaminie praktycznym to widać od pierwszej chwili.",
    related: [{"href":"/egzamin/zasady-bezpieczenstwa-na-strzelnicy","label":"Zasady bezpieczeństwa na strzelnicy"},{"href":"/egzamin/obowiazki-prowadzacego-strzelanie-bezpieczenstwo-na-strzelnicy","label":"Obowiązki prowadzącego strzelanie"}],
    sections: [
      {
        id: "komendy-i-procedury-strzeleckie",
        heading: "Komendy i procedury strzeleckie",
        questions: [
          {
            slug: "moze-wydac-komende-stop",
            q: "Kto może wydać komendę „STOP”?",
            blocks: [
              { type: 'p', text: "Każda osoba znajdująca się na strzelnicy ma prawo wydać komendę „STOP”, jeśli zauważy sytuację zagrażającą bezpieczeństwu. Komenda ta natychmiast przerywa wszelkie czynności - to zasada nadrzędna w każdej sytuacji, niezależnie od statusu uczestnika." },
            ],
          },
          {
            slug: "mozna-przyprowadzic-zwierzeta-na-strzelnice",
            q: "Czy można przyprowadzić zwierzęta na strzelnicę?",
            blocks: [
              { type: 'p', text: "Zwierzęta mogą przebywać na terenie strzelnicy tylko pod ścisłym nadzorem i na uwięzi. Opiekun musi zapewnić, że ich obecność nie zakłóca porządku i nie naraża innych uczestników na ryzyko. W kontekście egzaminu na broń pytanie to sprawdza znajomość zasad organizacyjnych." },
            ],
          },
          {
            slug: "co-oznacza-komenda-rozladuj",
            q: "Co oznacza komenda „ROZŁADUJ”?",
            blocks: [
              { type: 'p', text: "„ROZŁADUJ” to polecenie obowiązkowego rozbrojenia broni: należy wyjąć magazynek, usunąć nabój z komory, otworzyć zamek i odłożyć broń w bezpiecznym kierunku - zwykle lufą w stronę tarcz. To jedna z kluczowych procedur przed uzyskaniem pozwolenia na broń ." },
            ],
          },
          {
            slug: "mozna-zostawic-zaladowana-bron-podczas-przerwy",
            q: "Czy można zostawić załadowaną broń podczas przerwy?",
            blocks: [
              { type: 'p', text: "Bezwzględnie nie. Załadowana broń bez kontroli jest zagrożeniem życia. Odłożenie jej w takim stanie, nawet na chwilę, może być podstawą do przerwania egzaminu lub cofnięcia uprawnień." },
            ],
          },
          {
            slug: "prawidlowo-odkladac-bron-w-trakcie-konkurencji",
            q: "Jak prawidłowo odkładać broń w trakcie konkurencji?",
            blocks: [
              { type: 'p', text: "Po rozładowaniu - należy wyjąć magazynek, usunąć nabój z komory, otworzyć zamek i skierować lufę w stronę tarcz. Niewłaściwe odkładanie broni to częsty błąd kandydatów podczas egzaminu na patent strzelecki ." },
            ],
          },
          {
            slug: "robic-natychmiast-po-uslyszeniu-komendy-stop",
            q: "Co robić natychmiast po usłyszeniu komendy „STOP”?",
            blocks: [
              { type: 'p', text: "Należy natychmiast przestać strzelać, zabezpieczyć broń i zachować spokój. Brak reakcji na tę komendę może skutkować wykluczeniem z egzaminu lub nawet odpowiedzialnością dyscyplinarną." },
            ],
          },
          {
            slug: "co-oznacza-komenda-laduj",
            q: "Co oznacza komenda „ŁADUJ”?",
            blocks: [
              { type: 'p', text: "To sygnał do załadowania magazynka, włożenia go do broni i wprowadzenia naboju do komory. Po tej komendzie nie wolno jednak jeszcze strzelać - czekamy na „OGIEŃ”." },
            ],
          },
          {
            slug: "strzelanie-na-sucho-jest-dozwolone",
            q: "Czy „strzelanie na sucho” jest dozwolone?",
            blocks: [
              { type: 'p', text: "Tak, ale tylko w wyznaczonym miejscu i za wyraźną zgodą prowadzącego strzelanie. \"Na sucho\" oznacza bez amunicji - to technika treningowa, ale musi odbywać się zgodnie z regulaminem." },
            ],
          },
        ],
      },
      {
        id: "obowiazki-i-zachowania-uczestnikow",
        heading: "Obowiązki i zachowania uczestników",
        questions: [
          {
            slug: "osoba-naruszajaca-regulamin-moze-zostac-usunieta-ze",
            q: "Czy osoba naruszająca regulamin może zostać usunięta ze strzelnicy?",
            blocks: [
              { type: 'p', text: "Tak. Prowadzący ma prawo natychmiast wyprosić uczestnika, który nie przestrzega zasad. Dotyczy to również kandydatów przystępujących do egzaminu na broń ." },
            ],
          },
          {
            slug: "trzeba-zrobic-z-bronia-po-zakonczeniu-strzelania",
            q: "Co trzeba zrobić z bronią po zakończeniu strzelania?",
            blocks: [
              { type: 'p', text: "Należy ją rozładować, otworzyć komorę, pokazać do kontroli i bezwzględnie opuścić stanowisko dopiero po zatwierdzeniu przez prowadzącego. To obowiązek zarówno regulaminowy, jak i prawny." },
            ],
          },
          {
            slug: "komenda-przerywa-sie-strzelanie",
            q: "Jaką komendą przerywa się strzelanie?",
            blocks: [
              { type: 'p', text: "„STOP” - to sygnał natychmiastowego wstrzymania wszelkiej aktywności. Nie wolno jej ignorować pod żadnym pozorem - to jedno z pytań obowiązkowych na patencie strzeleckim ." },
            ],
          },
          {
            slug: "kiedy-nalezy-rozladowac-bron",
            q: "Kiedy należy rozładować broń?",
            blocks: [
              { type: 'p', text: "Bezpośrednio po zakończeniu serii lub na komendę „ROZŁADUJ”. Nawet jeśli strzelanie trwało tylko chwilę - broń musi zostać zabezpieczona." },
            ],
          },
          {
            slug: "dokladnie-zrobic-po-laduj",
            q: "Co dokładnie zrobić po „ŁADUJ”?",
            blocks: [
              { type: 'p', text: "Załadować, wprowadzić nabój do komory, zabezpieczyć broń i czekać na sygnał do rozpoczęcia ognia. Strzelanie bez komendy to poważne naruszenie." },
            ],
          },
          {
            slug: "jak-wyglada-zakonczenie-strzelania",
            q: "Jak wygląda zakończenie strzelania?",
            blocks: [
              { type: 'p', text: "Broń musi być rozładowana, przedstawiona do kontroli, a następnie przenoszona z otwartą komorą i bez magazynka. Każde odstępstwo grozi wykluczeniem z zawodów lub egzaminu." },
            ],
          },
          {
            slug: "mozna-celowac-do-przypadkowych-przedmiotow",
            q: "Czy można celować do przypadkowych przedmiotów?",
            blocks: [
              { type: 'p', text: "Nie. Broń można kierować tylko w stronę celów wyznaczonych przez organizatora. Celowanie w inne osoby lub przedmioty to najpoważniejsze naruszenie zasad." },
            ],
          },
        ],
      },
      {
        id: "rola-prowadzacego-i-regulamin",
        heading: "Rola prowadzącego i regulamin",
        questions: [
          {
            slug: "podstawowe-obowiazki-prowadzacego-strzelanie",
            q: "Jakie są podstawowe obowiązki prowadzącego strzelanie?",
            blocks: [
              { type: 'p', text: "Czuwanie nad bezpieczeństwem, kontrola broni, prowadzenie książki ewidencyjnej i udzielanie komend. Prowadzący odpowiada również za przebieg egzaminu na patent strzelecki i może przerwać go w dowolnym momencie." },
            ],
          },
          {
            slug: "zapoznanie-sie-z-regulaminem-jest-obowiazkowe",
            q: "Czy zapoznanie się z regulaminem jest obowiązkowe?",
            blocks: [
              { type: 'p', text: "Tak. Każdy uczestnik - niezależnie od doświadczenia - musi podpisać oświadczenie o zapoznaniu się z regulaminem. Bez tego nie można rozpocząć strzelania." },
            ],
          },
          {
            slug: "zachowania-sa-bezwzglednie-zakazane",
            q: "Jakie zachowania są bezwzględnie zakazane?",
            blocks: [
              { type: 'p', text: "Niedozwolone jest dotykanie cudzej broni bez zgody, wchodzenie na stanowiska bez pozwolenia oraz lekceważenie poleceń prowadzącego. Naruszenia mogą zakończyć się cofnięciem pozwolenia na broń ." },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/test-bezpieczenstwo-strzelnica-egzamin-na-bron",
    part: 6,
    h1: "Broń sportowa i amunicja ISSF - pytania na patent strzelecki (część 6)",
    lead: "Szósta część dotyczy broni sportowej i amunicji w ujęciu przepisów ISSF: danych rejestrowych, dopuszczalnych rodzajów amunicji oraz tego, czego w zawodach użyć nie wolno. To zakres, który odróżnia strzelca sportowego od posiadacza broni „na papierze\".",
    summary: "Przepisy ISSF wyglądają na drobiazgowe, ale pytania egzaminacyjne dotyczą zwykle tych samych kilku parametrów. Warto je przerobić w teście, a nie tylko przeczytać.",
    related: [{"href":"/egzamin/strzelectwo-sportowe-issf-przewodnik-dla-poczatkujacych","label":"Strzelectwo sportowe ISSF - przewodnik"},{"to":"/patent-strzelecki","label":"Patent strzelecki - wymagania i przebieg egzaminu"}],
    sections: [
      {
        id: "dane-rejestrowe-i-definicje-amunicji",
        heading: "Dane rejestrowe i definicje amunicji",
        questions: [
          {
            slug: "dane-musza-znalezc-sie-w-ksiazce-rejestru-pobytu-na",
            q: "Jakie dane muszą znaleźć się w książce rejestru pobytu na strzelnicy?",
            blocks: [
              { type: 'p', text: "Imię i nazwisko osoby korzystającej, numer pozwolenia na broń oraz organ wydający pozwolenie. Jeśli osoba nie posiada pozwolenia - podaje adres zamieszkania." },
            ],
          },
          {
            slug: "czym-jest-amunicja-bocznego-zaplonu",
            q: "Czym jest amunicja bocznego zapłonu?",
            blocks: [
              { type: 'p', text: "Amunicja, w której zapłon następuje przez uderzenie w obrzeże łuski. Typowa dla kalibru .22 LR - często używana w strzelectwie sportowym i rekreacyjnym." },
            ],
          },
          {
            slug: "czym-jest-amunicja-centralnego-zaplonu",
            q: "Czym jest amunicja centralnego zapłonu?",
            blocks: [
              { type: 'p', text: "Amunicja, w której spłonka znajduje się w centralnej części denka łuski. Stosowana w broni bojowej, myśliwskiej i sportowej - zapewnia większą moc i efektywność." },
            ],
          },
        ],
      },
      {
        id: "amunicja-i-bron-dopuszczona-przez-issf",
        heading: "Amunicja i broń dopuszczona przez ISSF",
        questions: [
          {
            slug: "amunicje-dopuszcza-issf-do-zawodow",
            q: "Jaką amunicję dopuszcza ISSF do zawodów?",
            blocks: [
              { type: 'p', text: "Amunicję bocznego zapłonu o kalibrze 5,6 mm (.22 LR), wykonaną z miękkiego ołowiu. Musi spełniać wymogi dotyczące energii pocisku i przeznaczenia sportowego." },
            ],
          },
          {
            slug: "ktora-bron-ma-najwiekszy-kaliber-wagomiarowy",
            q: "Która broń ma największy kaliber wagomiarowy?",
            blocks: [
              { type: 'p', text: "Broń kal. 12 - ponieważ w przypadku kalibrów wagomiarowych mniejszy numer oznacza większą średnicę przewodu lufy. Kal. 10 będzie większy niż 12, ale 12 to górna granica dopuszczalna w ISSF." },
            ],
          },
          {
            slug: "bron-pneumatyczna-dopuszcza-issf",
            q: "Jaką broń pneumatyczną dopuszcza ISSF?",
            blocks: [
              { type: 'p', text: "Masa do 1500 g, minimalny opór języka spustowego 500 g, maksymalne wymiary 420×200×50 mm." },
              { type: 'p', text: "Masa do 5500 g, długość celownika do 850 mm, dopuszczalny dowolny typ mechanizmu spustowego." },
            ],
          },
          {
            slug: "bron-palna-bocznego-zaplonu-dopuszcza-issf",
            q: "Jaką broń palną bocznego zapłonu dopuszcza ISSF?",
            blocks: [
              { type: 'p', text: "Pistolety i rewolwery kal. .22 LR, karabinki do 762 mm długości lufy i 8000 g masy. Stosowana w konkurencjach karabinowych i pistoletowych dla juniorów i dorosłych." },
            ],
          },
          {
            slug: "bron-srutowa-moze-byc-uzywana-w-issf",
            q: "Czy broń śrutowa może być używana w ISSF?",
            blocks: [
              { type: 'p', text: "Tak. Dozwolona jest broń gładkolufowa kal. 12 lub mniejszym (czyli numer większy), z komorą nabojową do 76 mm. Wykorzystywana w trapie i skeecie." },
            ],
          },
          {
            slug: "bron-centralnego-zaplonu-dopuszcza-issf",
            q: "Jaką broń centralnego zapłonu dopuszcza ISSF?",
            blocks: [
              { type: 'p', text: "Kalibry od 7,62 mm (.30\") do 9,65 mm (.38\"). Maksymalna masa: 1400 g. Lufa: max 153 mm długości." },
              { type: 'p', text: "Kaliber do 8 mm (.315\"), maksymalna masa: 8000 g." },
            ],
          },
        ],
      },
      {
        id: "amunicja-niedozwolona-przez-issf",
        heading: "Amunicja niedozwolona przez ISSF",
        questions: [
          {
            slug: "amunicji-nie-wolno-stosowac-w-zawodach-issf",
            q: "Jakiej amunicji nie wolno stosować w zawodach ISSF?",
            blocks: [
              { type: 'p', text: "Amunicji zawierającej stalowe rdzenie, pociski przeciwpancerne, zapalające lub wybuchowe. Niedozwolona jest także amunicja przekraczająca limity energii określone w regulaminach." },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/bron-sportowa-i-amunicja-egzamin-na-pozwolenie",
    part: 7,
    h1: "Egzamin na pozwolenie na broń - parametry broni i amunicji ISSF (część 7)",
    lead: "Siódma część to parametry techniczne: kalibry, opór spustu, wymiary broni i amunicja niedozwolona w zawodach. Pytania sprowadzają się zwykle do konkretnych wartości liczbowych, więc liczy się precyzja, a nie ogólne rozumienie tematu.",
    summary: "Liczb z tej części nie da się wydedukować - trzeba je po prostu utrwalić powtórkami. Najszybciej działa rozwiązywanie tych samych pytań do skutku.",
    related: [{"href":"/egzamin/konkurencje-pistoletowe-issf-przepisy-i-wymagania","label":"Konkurencje pistoletowe ISSF - przepisy"},{"href":"/egzamin/budowa-broni-i-amunicji-kompendium-dla-strzelcow","label":"Budowa broni i amunicji - kompendium"}],
    sections: [
      {
        id: "zakazana-amunicja-i-definicje-kalibrow",
        heading: "Zakazana amunicja i definicje kalibrów",
        questions: [
          {
            slug: "amunicji-nie-mozna-uzywac-w-zawodach-issf",
            q: "Jakiej amunicji nie można używać w zawodach ISSF?",
            blocks: [
              { type: 'p', text: "W zawodach organizowanych pod patronatem ISSF obowiązują bardzo restrykcyjne zasady dotyczące rodzaju używanej amunicji. Niedozwolona jest amunicja:" },
              { type: 'ul', items: ["wyposażona w pociski podkalibrowe lub zawierające elementy z tworzyw sztucznych,","zawierająca materiały łatwopalne, zapalające lub wybuchowe, które mogłyby zagrażać bezpieczeństwu uczestników,","wykonana z wyjątkowo twardych metali, takich jak wolfram czy jego stopy, ze względu na ich niszczycielską moc,","amunicja wytwarzana poza profesjonalnymi fabrykami, z wyjątkiem amunicji elaborowanej do własnego użytku przez osoby posiadające odpowiednie pozwolenia na broń i umiejętności techniczne."] },
              { type: 'p', text: "Wszystko po to, by zapewnić wyrównane szanse zawodnikom i pełne bezpieczeństwo podczas rywalizacji." },
            ],
          },
          {
            slug: "czym-jest-kaliber-broni",
            q: "Czym jest kaliber broni?",
            blocks: [
              { type: 'p', text: "Kaliber broni to podstawowy parametr techniczny określający średnicę wewnętrzną przewodu lufy lub średnicę pocisku, który jest do niej dostosowany. Podaje się go najczęściej w milimetrach (np. 5,6 mm) lub w calach (np. .22\"). Znajomość kalibru jest niezbędna do prawidłowego doboru amunicji i wpływa bezpośrednio na celność, energię pocisku oraz zastosowanie danego typu broni w praktyce strzeleckiej." },
            ],
          },
          {
            slug: "co-oznacza-kaliber-wagomiarowy",
            q: "Co oznacza kaliber wagomiarowy?",
            blocks: [
              { type: 'p', text: "Kaliber wagomiarowy to tradycyjny sposób określania średnicy lufy broni gładkolufowej. Oznacza on liczbę ołowianych kul, które można odlać z jednego funta ołowiu, przy założeniu, że każda kula ma średnicę odpowiadającą średnicy lufy. Przykład: dla kalibru 12, z jednego funta odlewa się 12 kul o średnicy przewodu lufy broni myśliwskiej. Im wyższa liczba kalibru wagomiarowego, tym mniejsza średnica lufy." },
            ],
          },
        ],
      },
      {
        id: "parametry-broni-w-konkurencjach-issf",
        heading: "Parametry broni w konkurencjach ISSF",
        questions: [
          {
            slug: "jest-maksymalna-dlugosc-lufy-pistoletu-dowolnego",
            q: "Jaka jest maksymalna długość lufy pistoletu dowolnego?",
            blocks: [
              { type: 'p', text: "W konkurencji pistoletu dowolnego przepisy ISSF nie nakładają ograniczeń co do maksymalnej długości lufy. Zawodnik ma pełną swobodę w doborze konstrukcji, o ile broń spełnia wszystkie inne wymagania regulaminowe, takie jak dopuszczalny kaliber czy bezpieczeństwo użytkowania. Pozwala to na wykorzystywanie specjalistycznych modeli optymalizowanych pod kątem stabilności i celności." },
            ],
          },
          {
            slug: "jest-maksymalna-dlugosc-lufy-pistoletu-sportowego",
            q: "Jaka jest maksymalna długość lufy pistoletu sportowego?",
            blocks: [
              { type: 'p', text: "W przypadku pistoletu sportowego obowiązuje ścisły limit długości lufy - maksymalnie 153 mm. Przekroczenie tej wartości skutkuje niedopuszczeniem zawodnika do startu. Ograniczenie to ma na celu zapewnienie równych warunków rywalizacji, zapobiegając nadmiernym przewagom wynikającym z długości przyrządów celowniczych lub stabilności konstrukcji." },
            ],
          },
          {
            slug: "jest-maksymalna-masa-karabinu-pneumatycznego",
            q: "Jaka jest maksymalna masa karabinu pneumatycznego?",
            blocks: [
              { type: 'p', text: "Karabin pneumatyczny używany w konkurencjach ISSF, wraz ze wszystkimi zamontowanymi akcesoriami (takimi jak celowniki optyczne, ciężarki balansujące, paski czy szyny montażowe), nie może ważyć więcej niż 5,5 kg. Limit ten wymusza kompromis pomiędzy stabilnością broni a jej poręcznością i komfortem użytkowania w czasie długich serii strzeleckich." },
            ],
          },
          {
            slug: "istnieje-ograniczenie-masy-pistoletu-dowolnego",
            q: "Czy istnieje ograniczenie masy pistoletu dowolnego?",
            blocks: [
              { type: 'p', text: "W konkurencji pistoletu dowolnego przepisy ISSF nie wprowadzają limitu masy broni. Oznacza to, że zawodnik może wybrać konstrukcję dowolnie ciężką lub lekką, dostosowując broń do swoich preferencji technicznych i stylu strzelania. Należy jednak pamiętać, że nadmierna masa może wpłynąć na zmęczenie mięśni i precyzję podczas długich serii." },
            ],
          },
          {
            slug: "jest-maksymalna-masa-pistoletu-sportowego",
            q: "Jaka jest maksymalna masa pistoletu sportowego?",
            blocks: [
              { type: 'p', text: "Pistolet sportowy w zawodach ISSF nie może ważyć więcej niż 1400 g, licząc razem z uchwytem, spustem i innymi zamontowanymi elementami. Przekroczenie tej granicy oznacza, że broń nie spełnia kryteriów regulaminowych i zawodnik nie zostanie dopuszczony do konkurencji. Limit masy wymusza na zawodnikach wybór lekkich, wyważonych konstrukcji sprzyjających szybkiemu i precyzyjnemu strzelaniu." },
            ],
          },
        ],
      },
      {
        id: "opor-spustu-i-wymiary-broni",
        heading: "Opór spustu i wymiary broni",
        questions: [
          {
            slug: "jest-minimalny-opor-jezyka-spustowego-w-pistolecie-dowolnym",
            q: "Jaki jest minimalny opór języka spustowego w pistolecie dowolnym?",
            blocks: [
              { type: 'p', text: "Brak minimalnego oporu - zawodnik może ustawić go według własnych preferencji." },
            ],
          },
          {
            slug: "jest-minimalny-opor-jezyka-spustowego-w-pistolecie",
            q: "Jaki jest minimalny opór języka spustowego w pistolecie pneumatycznym?",
            blocks: [
              { type: 'p', text: "500 g - wartość ta jest wymagana przepisami ISSF dla bezpieczeństwa." },
            ],
          },
          {
            slug: "jest-minimalny-opor-jezyka-spustowego-w-pistolecie-sportowym",
            q: "Jaki jest minimalny opór języka spustowego w pistolecie sportowym?",
            blocks: [
              { type: 'p', text: "1000 g - ustalony minimalny opór zapobiega przypadkowym strzałom i standardyzuje wyposażenie zawodników." },
            ],
          },
          {
            slug: "wymiary-musi-spelniac-pistolet-sportowy",
            q: "Jakie wymiary musi spełniać pistolet sportowy?",
            blocks: [
              { type: 'p', text: "Musi zmieścić się w pudełku 300 × 150 × 50 mm - sprawdzane podczas kontroli sprzętu przed zawodami." },
            ],
          },
          {
            slug: "wymiary-musi-spelniac-pistolet-pneumatyczny",
            q: "Jakie wymiary musi spełniać pistolet pneumatyczny?",
            blocks: [
              { type: 'p', text: "Wymiary nie mogą przekraczać 420 × 200 × 50 mm - to jedno z podstawowych kryteriów dopuszczenia do konkurencji." },
            ],
          },
        ],
      },
      {
        id: "inne-zasady-issf",
        heading: "Inne zasady ISSF",
        questions: [
          {
            slug: "mozna-uzywac-rewolweru-w-konkurencji-pistolet-sportowy-25-m",
            q: "Czy można używać rewolweru w konkurencji „Pistolet sportowy 25 m”?",
            blocks: [
              { type: 'p', text: "Tak - pod warunkiem że spełnia wymagania ISSF: kaliber, masa, długość i opór spustu." },
            ],
          },
          {
            slug: "naboi-laduje-sie-do-pistoletu-dowolnego",
            q: "Ile naboi ładuje się do pistoletu dowolnego?",
            blocks: [
              { type: 'p', text: "Jeden - to konkurencja precyzyjna, w której każdy strzał jest oddawany osobno." },
            ],
          },
          {
            slug: "zasady-dotyczace-ubioru-zawodnikow",
            q: "Jakie są zasady dotyczące ubioru zawodników?",
            blocks: [
              { type: 'p', text: "Odzież nie może wspomagać zawodnika ani stabilizować pozycji. Zakazane są buty zakrywające kostki, kamizelki usztywniające, pasy stabilizujące itp." },
            ],
          },
          {
            slug: "czym-jest-konkurencja-karabin-dowolny-60-strzalow-lezac",
            q: "Czym jest konkurencja „karabin dowolny 60 strzałów leżąc”?",
            blocks: [
              { type: 'p', text: "To jedna z klasycznych konkurencji ISSF: 60 strzałów z karabinu palnego na odległość 50 metrów w pozycji leżącej. Każdy strzał jest punktowany indywidualnie." },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/testy-strzeleckie-na-pozwolenie-na-bron",
    part: 8,
    h1: "Testy strzeleckie na pozwolenie na broń - konkurencje ISSF (część 8)",
    lead: "Ósma część obejmuje konkurencje ISSF, postawy strzeleckie i odległości w poszczególnych strzelaniach, a także przepisy karne dotyczące broni. Łączy więc wiedzę sportową z odpowiedzialnością prawną - dwa obszary, o które pyta się na egzaminie równie chętnie.",
    summary: "Konkurencje i postawy najłatwiej zapamiętać parami: dystans plus rodzaj broni. Przepisy karne wracają jeszcze w części dziewiątej.",
    related: [{"href":"/egzamin/konkurencje-na-50-metrow-w-zawodach-strzeleckich-issf","label":"Konkurencje na 50 metrów - ISSF"},{"href":"/egzamin/postawy-strzeleckie-w-konkurencjach-karabinowych-issf","label":"Postawy strzeleckie w konkurencjach karabinowych"}],
    sections: [
      {
        id: "konkurencje-strzeleckie-i-postawy",
        heading: "Konkurencje strzeleckie i postawy",
        questions: [
          {
            slug: "konkurencje-naleza-do-strzelby-gladkolufowej",
            q: "Jakie konkurencje należą do strzelby gładkolufowej?",
            blocks: [
              { type: 'p', text: "Do konkurencji z użyciem strzelby gładkolufowej zaliczamy: skeet, trap, double trap oraz automatic trap . Wszystkie polegają na strzelaniu do rzutków, czyli ceramicznych dysków, wyrzucanych z określonych punktów pod różnymi kątami. Strzelec musi wykazać się refleksem i precyzją w warunkach zmiennej dynamiki." },
            ],
          },
          {
            slug: "mozna-zaladowac-magazynek-podczas-zawodow-na-25-m",
            q: "Kiedy można załadować magazynek podczas zawodów na 25 m?",
            blocks: [
              { type: 'p', text: "Magazynek można załadować dopiero po wydaniu komendy „ ŁADUJ ” przez prowadzącego strzelanie. Załadowanie przed tą komendą jest poważnym naruszeniem regulaminu i może skutkować dyskwalifikacją." },
            ],
          },
          {
            slug: "odleglosci-w-konkurencjach-pistoletowych-issf",
            q: "Jakie są odległości w konkurencjach pistoletowych ISSF?",
            blocks: [
              { type: 'p', text: "Standardowe odległości to 10 m, 25 m i 50 m . Dobór dystansu zależy od rodzaju konkurencji (np. pistolet pneumatyczny - 10 m, sportowy - 25 m, dowolny - 50 m)." },
            ],
          },
          {
            slug: "odleglosci-w-konkurencjach-karabinowych-issf",
            q: "Jakie są odległości w konkurencjach karabinowych ISSF?",
            blocks: [
              { type: 'p', text: "W konkurencjach karabinowych wyróżnia się trzy odległości: 10 m, 50 m i 300 m . Zawody na 300 m odbywają się głównie na poziomie międzynarodowym i wymagają szczególnej precyzji." },
            ],
          },
          {
            slug: "odleglosci-obowiazuja-w-konkurencjach-issf",
            q: "Jakie odległości obowiązują w konkurencjach ISSF?",
            blocks: [
              { type: 'p', text: "Oficjalne odległości to: 10, 25, 50 oraz 300 metrów . Obowiązują w zależności od konkurencji, rodzaju broni i kategorii zawodnika (junior/senior)." },
            ],
          },
          {
            slug: "na-jaka-odleglosc-strzela-sie-w-konkurencji-pistolet",
            q: "Na jaką odległość strzela się w konkurencji pistolet sportowy 30+30?",
            blocks: [
              { type: 'p', text: "Konkurencja 30+30 rozgrywana jest na 25 metrów . Zawodnik oddaje 60 strzałów - 30 w tempie wolnym, 30 w szybkim." },
            ],
          },
          {
            slug: "wyglada-konkurencja-pistolet-szybkostrzelny",
            q: "Jak wygląda konkurencja pistolet szybkostrzelny?",
            blocks: [
              { type: 'p', text: "W tej konkurencji strzela się do pięciu tarcz z odległości 25 metrów . Czas reakcji i rytm oddawania strzałów są tu kluczowe - to sprawdzian refleksu i precyzji." },
            ],
          },
          {
            slug: "konkurencje-odbywaja-sie-na-10-metrow",
            q: "Jakie konkurencje odbywają się na 10 metrów?",
            blocks: [
              { type: 'p', text: "Karabin i pistolet pneumatyczny - zawodnik strzela z pozycji stojącej . To najczęstsza konkurencja wśród juniorów i początkujących." },
            ],
          },
          {
            slug: "konkurencje-odbywaja-sie-na-25-metrow",
            q: "Jakie konkurencje odbywają się na 25 metrów?",
            blocks: [
              { type: 'p', text: "Konkurencje z pistoletem sportowym, centralnego zapłonu i szybkostrzelne - wszystkie wymagają opanowania precyzji i tempa." },
            ],
          },
          {
            slug: "konkurencje-odbywaja-sie-na-50-metrow",
            q: "Jakie konkurencje odbywają się na 50 metrów?",
            blocks: [
              { type: 'p', text: "Karabin dowolny, karabin sportowy i konkurencje z tarczą ruchomą. To konkurencje zaawansowane, wymagające szczególnego przygotowania." },
            ],
          },
          {
            slug: "postawy-w-konkurencjach-karabinowych",
            q: "Jakie są postawy w konkurencjach karabinowych?",
            blocks: [
              { type: 'p', text: "Leżąca - największa stabilność, stojąca - najtrudniejsza, klęcząca - kompromis między mobilnością a precyzją. W trójboju zawodnik strzela we wszystkich trzech postawach." },
            ],
          },
          {
            slug: "jakie-sa-dyscypliny-strzelectwa-sportowego",
            q: "Jakie są dyscypliny strzelectwa sportowego?",
            blocks: [
              { type: 'p', text: "Oficjalnie ISSF wyróżnia trzy podstawowe grupy: karabin, pistolet, strzelba . Każda z nich zawiera wiele konkurencji o różnych zasadach." },
            ],
          },
          {
            slug: "typy-pistoletow-dopuszcza-issf",
            q: "Jakie typy pistoletów dopuszcza ISSF?",
            blocks: [
              { type: 'p', text: "Dowolny, sportowy, szybkostrzelny, centralnego zapłonu oraz pneumatyczny - każda konkurencja ma ściśle określone wymagania techniczne." },
            ],
          },
          {
            slug: "na-czym-polega-konkurencja-skeet",
            q: "Na czym polega konkurencja SKEET?",
            blocks: [
              { type: 'p', text: "Strzelanie do rzutków wyrzucanych z dwóch wież (niskiej i wysokiej), w różnych konfiguracjach. Wymaga szybkiej oceny trajektorii i błyskawicznego oddania strzału." },
            ],
          },
          {
            slug: "na-czym-polega-konkurencja-trap",
            q: "Na czym polega konkurencja TRAP?",
            blocks: [
              { type: 'p', text: "Rzutki wyrzucane są z bunkra - w sposób losowy, pod różnymi kątami i wysokościami. Kluczowa jest znajomość rytmu i precyzja w pierwszym ułamku sekundy." },
            ],
          },
        ],
      },
      {
        id: "przepisy-karne-dotyczace-broni",
        heading: "Przepisy karne dotyczące broni",
        questions: [
          {
            slug: "kary-stosuje-sie-za-naruszenie-regulaminu-issf",
            q: "Jakie kary stosuje się za naruszenie regulaminu ISSF?",
            blocks: [
              { type: 'p', text: "W zależności od naruszenia - od ustnego ostrzeżenia , przez odjęcie punktów , aż po dyskwalifikację . Regulamin jasno określa zasady reakcji na nieprawidłowości." },
            ],
          },
          {
            slug: "grozi-za-noszenie-broni-po-spozyciu-alkoholu-lub-srodkow",
            q: "Co grozi za noszenie broni po spożyciu alkoholu lub środków psychoaktywnych?",
            blocks: [
              { type: 'p', text: "Kara aresztu lub grzywny , a także możliwość cofnięcia pozwolenia na broń. To jedno z najpoważniejszych naruszeń." },
            ],
          },
          {
            slug: "grozi-za-porzucenie-broni-lub-amunicji",
            q: "Co grozi za porzucenie broni lub amunicji?",
            blocks: [
              { type: 'p', text: "Do 2 lat pozbawienia wolności - zgodnie z Kodeksem karnym. Broń musi być stale nadzorowana, nawet chwilowe porzucenie może skutkować poważnymi konsekwencjami." },
            ],
          },
          {
            slug: "grozi-za-brak-rejestracji-lub-zlozenia-broni",
            q: "Co grozi za brak rejestracji lub złożenia broni?",
            blocks: [
              { type: 'p', text: "Grzywna lub areszt - każda broń musi być zarejestrowana w odpowiednim terminie (najczęściej 5 dni od nabycia)." },
            ],
          },
          {
            slug: "grozi-za-posiadanie-lub-zbycie-broni-pneumatycznej-bez",
            q: "Co grozi za posiadanie lub zbycie broni pneumatycznej bez rejestracji?",
            blocks: [
              { type: 'p', text: "Areszt lub grzywna . Dotyczy również osób nieposiadających pozwolenia na broń, które kupiły broń pneumatyczną powyżej 17 J." },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/issf-przepisy-egzamin-pozwolenie-na-bron",
    part: 9,
    h1: "Patent strzelecki - prawo karne i granice obrony koniecznej (część 9)",
    lead: "Ostatnia część dotyczy odpowiedzialności karnej: przestępstw i wykroczeń związanych z bronią, granic obrony koniecznej oraz konsekwencji naruszenia przepisów. To pytania, przy których liczy się rozumienie zasady, a nie zapamiętanie formułki.",
    summary: "Na tym kończy się cały zestaw pytań teoretycznych. Jeśli przerobiłeś wszystkie dziewięć części, zostaje najważniejsze - sprawdzenie się w warunkach testu na czas.",
    related: [{"href":"/egzamin/obrona-konieczna-i-stan-wyzszej-koniecznosci","label":"Obrona konieczna i stan wyższej konieczności"},{"href":"/egzamin/przepisy-karne-posiadanie-broni-w-polsce","label":"Przepisy karne - posiadanie broni w Polsce"}],
    sections: [
      {
        id: "przestepstwa-i-wykroczenia-zwiazane-z-bronia",
        heading: "Przestępstwa i wykroczenia związane z bronią",
        questions: [
          {
            slug: "grozi-za-brak-zgloszenia-zbycia-lub-utraty-broni-amunicji",
            q: "Co grozi za brak zgłoszenia zbycia lub utraty broni/amunicji?",
            blocks: [
              { type: 'p', text: "Zgodnie z ustawą o broni i amunicji, każda utrata, zbycie lub kradzież broni musi być niezwłocznie zgłoszona do organów Policji. Brak takiego zgłoszenia to wykroczenie zagrożone karą aresztu lub grzywny - nawet jeśli zdarzenie nie było zawinione." },
            ],
          },
          {
            slug: "grozi-za-brak-zgloszenia-zmiany-miejsca-stalego-pobytu",
            q: "Co grozi za brak zgłoszenia zmiany miejsca stałego pobytu?",
            blocks: [
              { type: 'p', text: "Osoba posiadająca broń lub pozwolenie na broń ma obowiązek poinformować właściwy organ w terminie 14 dni o zmianie miejsca zamieszkania. Niezgłoszenie zmiany adresu może skutkować karą grzywny lub aresztem, a także utratą pozwolenia." },
            ],
          },
          {
            slug: "grozi-za-nieumyslna-utrate-broni-lub-amunicji",
            q: "Co grozi za nieumyślną utratę broni lub amunicji?",
            blocks: [
              { type: 'p', text: "Nawet nieumyślne zgubienie broni (np. w transporcie) może skutkować karą grzywny, ograniczenia wolności albo pozbawienia wolności do roku. Odpowiedzialność karna wynika z samego faktu naruszenia zasad bezpieczeństwa." },
            ],
          },
        ],
      },
      {
        id: "obrona-konieczna-i-stan-wyzszej-koniecznosci",
        heading: "Obrona konieczna i stan wyższej konieczności",
        questions: [
          {
            slug: "czym-jest-stan-wyzszej-koniecznosci",
            q: "Czym jest stan wyższej konieczności?",
            blocks: [
              { type: 'p', text: "Stan wyższej konieczności występuje, gdy działamy w celu uchylenia bezpośredniego niebezpieczeństwa grożącego dobru chronionemu prawem, poświęcając inne dobro o mniejszej wartości. Przykład: złamanie przepisów w celu ratowania życia." },
            ],
          },
          {
            slug: "czym-jest-obrona-konieczna",
            q: "Czym jest obrona konieczna?",
            blocks: [
              { type: 'p', text: "To działanie podejmowane w celu odparcia bezpośredniego, bezprawnego zamachu na życie, zdrowie, mienie lub inne dobro. Prawo zezwala na obronę - nawet przy użyciu broni - jeśli atak nastąpił nagle i zagrażał istotnym wartościom." },
            ],
          },
          {
            slug: "sad-moze-odstapic-od-kary-za-przekroczenie-obrony-koniecznej",
            q: "Czy sąd może odstąpić od kary za przekroczenie obrony koniecznej?",
            blocks: [
              { type: 'p', text: "Tak. Jeśli przekroczenie granic obrony nastąpiło wskutek silnego wzburzenia lub strachu, który był uzasadniony sytuacją (np. nocny atak w mieszkaniu), sąd może całkowicie odstąpić od wymierzenia kary." },
            ],
          },
          {
            slug: "sad-moze-zlagodzic-kare-za-przekroczenie-stanu-wyzszej",
            q: "Czy sąd może złagodzić karę za przekroczenie stanu wyższej konieczności?",
            blocks: [
              { type: 'p', text: "Może. Zwłaszcza jeśli okoliczności wskazują, że działanie podjęte było w dobrej wierze i miało na celu ratowanie życia lub zdrowia, nawet jeśli proporcje dobra poświęcanego i ratowanego były niewłaściwe." },
            ],
          },
        ],
      },
      {
        id: "naruszenia-przepisow-i-ich-konsekwencje",
        heading: "Naruszenia przepisów i ich konsekwencje",
        questions: [
          {
            slug: "grozi-za-noszenie-broni-z-naruszeniem-ograniczenia-lub",
            q: "Co grozi za noszenie broni z naruszeniem ograniczenia lub zakazu?",
            blocks: [
              { type: 'p', text: "Posiadacz broni, który łamie zakaz noszenia jej przy sobie (np. w przestrzeni publicznej, mimo warunkowego pozwolenia), może podlegać karze grzywny lub aresztu. Może to też skutkować cofnięciem pozwolenia." },
            ],
          },
          {
            slug: "grozi-za-uzycie-broni-poza-strzelnica-w-celach",
            q: "Co grozi za użycie broni poza strzelnicą w celach szkoleniowych/sportowych?",
            blocks: [
              { type: 'p', text: "Zgodnie z przepisami, broń do celów sportowych może być używana wyłącznie na strzelnicach. Jej użycie poza nimi, nawet przez osobę z pozwoleniem, jest naruszeniem i może skutkować sankcją karną." },
            ],
          },
          {
            slug: "grozi-za-przywoz-broni-spoza-ue-bez-dokumentu-konsula",
            q: "Co grozi za przywóz broni spoza UE bez dokumentu konsula?",
            blocks: [
              { type: 'p', text: "Każda broń przywożona spoza Unii Europejskiej musi być legalizowana dokumentem wydanym przez konsula RP. Brak tego dokumentu oznacza naruszenie przepisów celnych i może skutkować aresztem lub grzywną." },
            ],
          },
          {
            slug: "grozi-za-produkcje-lub-handel-bronia-bez-zezwolenia",
            q: "Co grozi za produkcję lub handel bronią bez zezwolenia?",
            blocks: [
              { type: 'p', text: "Nielegalna produkcja, przetwarzanie lub handel bronią palną to przestępstwo zagrożone karą od 1 do 10 lat pozbawienia wolności. Dotyczy to zarówno działalności komercyjnej, jak i produkcji w celach osobistych." },
            ],
          },
          {
            slug: "grozi-za-nielegalne-posiadanie-broni-palnej-lub-amunicji",
            q: "Co grozi za nielegalne posiadanie broni palnej lub amunicji?",
            blocks: [
              { type: 'p', text: "Zgodnie z Kodeksem karnym, posiadanie broni palnej lub amunicji bez wymaganego pozwolenia jest przestępstwem. Grozi za nie kara pozbawienia wolności od 6 miesięcy do 8 lat." },
            ],
          },
        ],
      },
    ],
  },
];

export const TOTAL_PARTS = EXAM_PARTS.length;

export const getExamPart = path => EXAM_PARTS.find(p => p.path === path) || null;

export const countQuestions = part =>
  part.sections.reduce((sum, section) => sum + section.questions.length, 0);
